<script setup lang="ts">
import { computed, ref, watch } from "vue"
import { useRoute, useRouter } from "vue-router"
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/vue"
import { BellIcon } from "@heroicons/vue/24/outline"
import { MagnifyingGlassIcon } from "@heroicons/vue/24/solid"
import { useAppContext } from "../../AppContext"
import { firstStringValue } from "../../routing-helper"
import UploadTrigger from "../../upload/UploadTrigger.vue"
import NFansLogoInvert from "../../components/NFansLogoInvert.vue"
import auth from "../../auth/keycloak"

const { communityIndicator } = useAppContext()

const router = useRouter()
const route = useRoute()
const searchQuery = ref("")

// Mirror the active query while on the search page; stay empty elsewhere so the
// list views' own ?query= filters don't bleed into the global search field.
watch(
  () => [route.name, route.query.query] as const,
  () => {
    searchQuery.value = route.name === "search" ? (firstStringValue(route.query.query) || "") : ""
  },
  { immediate: true }
)

function submitSearch(): void {
  const q = searchQuery.value.trim()
  router.push({ name: "search", query: q ? { query: q } : {} })
}

const bellColor = computed(() => {
  if (communityIndicator.value.pendingModerationCount > 0) return "text-error"
  if (communityIndicator.value.unreadCount > 0) return "text-primary"
  return "text-base-content/40"
})

const hasAnyNotification = computed(() =>
  communityIndicator.value.pendingModerationCount > 0 ||
  communityIndicator.value.unreadCount > 0
)

const username = computed(() => auth.userProfile.value?.username ?? "Unbekannt")

const usernameInitial = computed(() => {
  const p = auth.userProfile.value ?? {}
  const initials = [p.firstName, p.lastName]
    .filter(Boolean)
    .map(s => String(s).charAt(0).toUpperCase())
    .slice(0, 2)
    .join("")
  return initials || String(p.username ?? "?").charAt(0).toUpperCase()
})

const fullName = computed(() => {
  const p = auth.userProfile.value ?? {}
  return [p.firstName, p.lastName].filter(Boolean).join(" ").trim()
})

function logout() { auth.logout() }
</script>

<template>
  <header class="navbar bg-base-100 border-b border-base-300 h-16 min-h-16 sticky top-0 z-20 px-4 lg:px-6">
    <!-- Mobile wordmark; hidden on lg+ where the sidebar carries the brand -->
    <div class="flex-none lg:hidden">
      <NFansLogoInvert class="h-6 w-auto text-neutral" alt="NFans" />
    </div>
    <!-- Search, desktop only -->
    <div
      class="flex-1 hidden lg:flex items-center"
      data-testid="topbar-search"
    >
      <form class="relative w-full max-w-md text-base-content/60" @submit.prevent="submitSearch">
        <label for="topbar-search-input" class="sr-only">Suche</label>
        <MagnifyingGlassIcon
          class="h-5 w-5 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none z-10"
          aria-hidden="true"
        />
        <input
          id="topbar-search-input"
          v-model="searchQuery"
          data-testid="topbar-search-input"
          class="input input-ghost input-sm w-full pl-10"
          type="search"
          placeholder="Suche…"
        >
      </form>
    </div>
    <div class="flex-1 lg:hidden" />
    <router-link
      :to="{ name: 'search' }"
      data-testid="topbar-search-mobile"
      class="btn btn-ghost btn-sm btn-square lg:hidden"
      aria-label="Suche"
    >
      <MagnifyingGlassIcon class="h-5 w-5" aria-hidden="true" />
    </router-link>
    <div class="flex-none flex items-center gap-2">
      <UploadTrigger />
      <router-link
        v-slot="{ navigate }"
        :to="{ name: 'community' }"
        custom
      >
        <button
          type="button"
          class="btn btn-ghost btn-sm btn-square"
          :class="bellColor"
          :disabled="!hasAnyNotification"
          data-testid="topbar-bell"
          aria-label="Benachrichtigungen"
          @click="navigate"
        >
          <BellIcon
            class="h-5 w-5"
            aria-hidden="true"
          />
        </button>
      </router-link>
      <Menu as="div" class="relative">
        <MenuButton
          class="btn btn-ghost btn-circle btn-sm avatar avatar-placeholder"
          aria-label="Benutzermenü"
        >
          <div
            class="bg-neutral text-neutral-content w-8 h-8 rounded-full text-xs font-semibold"
            data-testid="topbar-avatar"
          >{{ usernameInitial }}</div>
        </MenuButton>
        <transition
          enter-active-class="transition ease-out duration-100"
          enter-from-class="opacity-0 scale-95"
          enter-to-class="opacity-100 scale-100"
          leave-active-class="transition ease-in duration-75"
          leave-from-class="opacity-100 scale-100"
          leave-to-class="opacity-0 scale-95"
        >
          <MenuItems
            class="absolute right-0 mt-2 w-48 bg-base-100 rounded-box shadow-lg ring-1 ring-base-300 py-1 focus:outline-none"
          >
            <div class="px-3 py-1.5 text-xs text-base-content/60 border-b border-base-300 truncate">
              {{ fullName || username }}
            </div>
            <MenuItem v-slot="{ active }">
              <a
                :href="auth.accountUrl()"
                :class="[active ? 'bg-base-200' : '', 'block px-3 py-2 text-sm']"
              >Profil</a>
            </MenuItem>
            <MenuItem v-slot="{ active }">
              <button
                type="button"
                :class="[active ? 'bg-base-200' : '', 'block w-full text-left px-3 py-2 text-sm']"
                @click="logout"
              >Abmelden</button>
            </MenuItem>
          </MenuItems>
        </transition>
      </Menu>
    </div>
  </header>
</template>
